import { useState } from 'react';
import { api } from '../../lib/api.ts';
import { Archive, Trash } from '../../lib/icons.tsx';
import { isArchived, type BankActions } from './bankShared.ts';
import type { BankItem } from '../../../shared/deck.ts';

/**
 * The Text facts category. Facts are copy the AI quotes verbatim, not files, so
 * they get their own editor rather than the asset grid — and archiving matters
 * here for a different reason: a stale number the AI can still quote onto a
 * client slide is worse than a missing one.
 */
export default function BankFacts({
  items,
  busy,
  actions,
}: {
  items: BankItem[];
  busy?: boolean;
  actions: BankActions;
}) {
  const { run, reload, pushToast, setConfirm, setItemStatus } = actions;
  const [adding, setAdding] = useState(false);

  async function createFact(label: string, text: string) {
    await run(async () => {
      await api.post('/api/bank/items', { category: 'facts', label, text });
      setAdding(false);
      await reload();
    }, 'Save failed');
  }

  async function saveFact(item: BankItem, label: string, text: string) {
    await run(async () => {
      await api.put(`/api/bank/items/${item.id}`, { label, text });
      await reload();
    }, 'Save failed');
  }

  function deleteFact(item: BankItem) {
    setConfirm({
      title: `Delete the "${item.label}" fact?`,
      body: 'The copy is removed from the bank. Slides that already quote it are untouched.',
      danger: true,
      confirmLabel: 'Delete fact',
      onConfirm: async () => {
        await run(async () => {
          await api.delete(`/api/bank/items/${item.id}`);
          await reload();
          pushToast(`Deleted "${item.label}"`);
        }, 'Delete failed');
        setConfirm(null);
      },
    });
  }

  return (
    <div className="bank__facts">
      <p className="muted bank__note">
        Boilerplate copy the AI can quote word-for-word on any slide: the about-RKH blurb, the client list, company
        facts. Keep each one short and self-contained. Archive a fact that has gone stale rather than leaving a wrong
        number where the AI can quote it.
      </p>
      {adding ? (
        <FactForm busy={busy} onSave={createFact} onCancel={() => setAdding(false)} />
      ) : (
        <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)} disabled={busy}>
          + Add fact
        </button>
      )}
      {items.length === 0 && !adding && <p className="muted bank__empty">Nothing in this view.</p>}
      {items.map((it) => (
        <FactForm
          key={it.id}
          fact={it}
          busy={busy}
          onSave={(label, text) => saveFact(it, label, text)}
          onDelete={() => deleteFact(it)}
          onToggleStatus={() => setItemStatus(it, isArchived(it) ? 'active' : 'archived')}
        />
      ))}
    </div>
  );
}

function FactForm({
  fact = null,
  busy,
  onSave,
  onCancel,
  onDelete,
  onToggleStatus,
}: {
  fact?: BankItem | null;
  busy?: boolean;
  onSave: (label: string, text: string) => void | Promise<void>;
  onCancel?: () => void;
  onDelete?: () => void;
  onToggleStatus?: () => void;
}) {
  const [label, setLabel] = useState(fact?.label || '');
  const [text, setText] = useState(fact?.text || '');
  const dirty = fact ? label !== fact.label || text !== (fact.text || '') : true;
  const archived = fact ? isArchived(fact) : false;
  return (
    <form
      className={'bank-fact' + (archived ? ' bank-fact--archived' : '')}
      onSubmit={(e) => {
        e.preventDefault();
        onSave(label.trim(), text);
      }}
    >
      <div className="bank-fact__head">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Fact name, e.g. About RKH"
          aria-label="Fact name"
        />
        <span className="bank-fact__actions">
          {archived && <span className="bank-card__badge bank-card__badge--inline">Archived</span>}
          {onCancel && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !label.trim() || !dirty}>
            Save
          </button>
          {onToggleStatus && (
            <button
              type="button"
              className={'iconbtn' + (archived ? ' is-active' : '')}
              onClick={onToggleStatus}
              aria-label={archived ? 'Restore fact' : 'Archive fact'}
              title={archived ? 'Restore to the bank' : 'Archive: keeps it here, stops the AI quoting it'}
              disabled={busy}
            >
              <Archive />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className="iconbtn"
              onClick={onDelete}
              aria-label="Delete fact"
              title="Delete"
              disabled={busy}
            >
              <Trash />
            </button>
          )}
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="The copy, exactly as it should appear on slides."
        aria-label="Fact text"
      />
    </form>
  );
}
